import { z } from "zod";
import GeminiProvider from "./ai/providers/GeminiProvider";
import { getEnvValue } from "../utils/env";

const MAX_LIST_ITEMS = 5;
const MAX_TIMELINE_MILESTONES = 8;
const PROMPT_LIST_LIMIT = 6;
const DEFAULT_TRANSLATION_TIMEOUT = 60000;
const DEFAULT_TIMELINE_LOOKBACK_DAYS = 45;
const DEFAULT_SAFETY_SETTINGS = [
  { category: "HARM_CATEGORY_HARASSMENT", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
  { category: "HARM_CATEGORY_HATE_SPEECH", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
  { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
  { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_MEDIUM_AND_ABOVE" }
];

class GeminiChatCompletionsAdapter {
  constructor(config) {
    this.provider = new GeminiProvider(config);
  }

  getModelName() {
    return this.provider.getModelName();
  }

  normalizeContent(content) {
    if (content == null) return '';
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .map(part => {
          if (typeof part === 'string') return part;
          if (part?.text) return part.text;
          try {
            return JSON.stringify(part);
          } catch (_) {
            return '';
          }
        })
        .filter(Boolean)
        .join('\n');
    }
    if (typeof content === 'object') {
      try {
        return JSON.stringify(content);
      } catch (_) {
        return '';
      }
    }
    return String(content);
  }

  normalizeMessages(messages = []) {
    const contents = [];
    const systemChunks = [];

    messages.forEach((message) => {
      if (!message) return;
      const normalized = this.normalizeContent(message.content);
      if (!normalized) return;

      if (message.role === 'system') {
        systemChunks.push(normalized);
        return;
      }

      contents.push({
        role: message.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: normalized }]
      });
    });

    const systemPrompt = systemChunks.join('\n\n').trim();
    if (systemPrompt) {
      if (contents.length === 0) {
        contents.push({ role: 'user', parts: [{ text: systemPrompt }] });
      } else {
        const firstText = contents[0].parts?.[0]?.text || '';
        contents[0] = {
          ...contents[0],
          parts: [{ text: `${systemPrompt}\n\n${firstText}`.trim() }]
        };
      }
    }

    if (contents.length === 0) {
      contents.push({
        role: 'user',
        parts: [{ text: 'You are Ravana Migration AI assistant.' }]
      });
    }

    return contents;
  }

  async create(params = {}) {
    const {
      messages = [],
      temperature = 0.2,
      top_p = 0.95,
      top_k = 40,
      max_completion_tokens = 2048,
      safetySettings
    } = params;

    const contents = this.normalizeMessages(messages);
    const generationConfig = {
      temperature,
      topP: top_p,
      topK: top_k,
      maxOutputTokens: Math.max(256, Math.min(max_completion_tokens || 2048, 8192))
    };

    if (temperature === undefined || temperature === null) {
      delete generationConfig.temperature;
    }

    const requestBody = {
      contents,
      generationConfig,
      safetySettings: safetySettings || DEFAULT_SAFETY_SETTINGS
    };

    const response = await this.provider.callGemini(requestBody, 'admin-service');
    const content = this.provider.extractCandidateText(response) || '';

    return {
      choices: [
        {
          message: {
            content
          }
        }
      ]
    };
  }
}

const CONSULT_SYSTEM_PROMPT = `You are Ravana Migration's AI immigration consultant. Collect all data required to complete the Ravana lead qualification wizard via natural-language chat.

Conversation rules:
- Always greet the user briefly, then confirm their preferred language (English/Tamil/Sinhala). Respond in that language once known; default to English.
- Ask one question at a time. Acknowledge the user's answer before moving to the next question.
- Required data sections: languagePreference, personalInfo(firstName,lastName,email,phone,dateOfBirth,nationality,currentCountry), countrySelection(selectedCountry,selectedPathway,immigrationPurpose,timeline), education(highestEducation,fieldOfStudy,graduationYear), experience(totalExperience,hasInternationalExperience,jobTitle,company,industry,skills), languageSkills(englishSpeaking,hasIELTS,ieltsScore if IELTS taken), financialInfo(educationBudget,fundingPlan,currency), contactPreference.
- Optional data (collect if offered): education.gpa, experience.skills.
- When the user uploads a CV (the context will set cvUploaded=true), confirm receipt and do NOT request a manual summary.
- If the user asks questions, answer briefly and keep gathering required information.
- Once all required data is gathered (and IELTS score if applicable), set shouldComplete=true and summarise the collected profile before submission.
- Never fabricate information. Only set fields you are certain about.

Response format:
Return strictly valid JSON (no markdown, no code fences) with:
{
  "reply": "message to display to the user (<=2 sentences)",
  "dataUpdates": {
    "section": { "field": "value" }
  },
  "pendingFields": [ { "section": "...", "field": "...", "label": "..." } ],
  "shouldComplete": true|false,
  "nextAction": "ask_question" | "await_upload" | "ready_to_submit",
  "suggestions": [ { "label": "Quick reply text", "value": "value to save" } ]
}

Chip Suggestion Rules:
- ALWAYS include 3-6 contextual "suggestions" (quick reply chips) for the current question
- Make suggestions intelligent based on the user's CV, profile, selected country, and pathway
- For countries: Suggest relevant destinations based on their education/experience
- For pathways: Suggest options that match their profile (e.g., if bachelor's degree → Study, if 5+ years exp → Skilled Worker)
- For immigration purpose: Suggest based on their pathway (Study→Education, Work→Employment/Career Development)
- For timeline: Offer realistic options based on their readiness (has IELTS → sooner, no IELTS → later)
- For budget: Suggest ranges in their stated currency or LKR equivalents
- For IELTS: If they mentioned English proficiency → suggest Yes, if education field → likely Yes
- Keep suggestions concise (2-4 words each)
- Make them actionable and contextually relevant

Examples:
- If asking "Target destination country?" and user has engineering degree → ["Germany", "Canada", "Australia", "UK"]
- If asking "Immigration objective?" and pathway is Study → ["Education", "Career Development", "Better Opportunities"]
- If asking "Timeline?" and they have IELTS → ["Within 3 months", "3-6 months", "Within 12 months"]

Guidance:
- Keep replies friendly, concise, and actionable.
- Use verbs in questions (e.g., "Could you share...").
- If data is unclear, ask a follow-up question instead of guessing.
- Highlight if any financial or document follow-up is needed.
- Support Tamil (ta) and Sinhala (si) output when requested; otherwise English.
- Encourage the user to share their WhatsApp/phone preference if contactPreference is missing.
- When shouldComplete=true, congratulate the user, describe next steps, and state that their lead will be submitted.`;

const PRICING_LIBRARY = {
  GERMANY_STUDY: {
    label: "Germany Study Programs",
    country: "Germany",
    price: "LKR 1,190,000",
    paymentStructure: "50% upfront, 50% after offer letter",
    token: "PRICING_GERMANY_STUDY"
  },
  GERMANY_CHANCENKARTE: {
    label: "Germany Chancenkarte / Skilled Worker",
    country: "Germany",
    price: "LKR 1,390,000",
    paymentStructure: "50% upfront, 50% after approval",
    token: "PRICING_GERMANY_CHANCENKARTE"
  },
  UK_STUDY: {
    label: "United Kingdom Study Programs",
    country: "UK",
    price: "LKR 400,000",
    paymentStructure: "50% upfront, 50% after offer letter",
    token: "PRICING_UK_STUDY"
  },
  CANADA_STUDY: {
    label: "Canada Study Programs",
    country: "Canada",
    price: "LKR 1,150,000",
    paymentStructure: "25% upfront, balance after arrival (loan supported)",
    token: "PRICING_CANADA_STUDY"
  }
};

const OBJECTION_LIBRARY = {
  BUDGET: {
    token: "OBJ_BUDGET_SUPPORT",
    summary: "Emphasize financing options, staged payments, and ROI of immigration investment."
  },
  TIMELINE: {
    token: "OBJ_TIMELINE_CLARITY",
    summary: "Clarify end-to-end process stages, realistic timelines, and proactive status updates."
  },
  COMPETITION: {
    token: "OBJ_COMPETITIVE_EDGE",
    summary: "Highlight Ravana's specialized expertise, success metrics, and white-glove service."
  },
  LANGUAGE: {
    token: "OBJ_LANGUAGE_SUPPORT",
    summary: "Clarify language requirements and available coaching/IELTS preparation support."
  }
};

const ANALYTICS_SCHEMA = z.object({
  conversionMetrics: z.object({
    conversionProbability: z.number().min(0).max(100),
    conversionLevel: z.enum(["Hot", "Warm", "Cold"]),
    estimatedDealSize: z.number().nonnegative(),
    estimatedCommission: z.number().nonnegative(),
    closeTimeframe: z.union([z.string(), z.number()]),
    confidenceLevel: z.string(),
    buyingSignals: z.array(z.string()).optional(),
    conversionBlockers: z.array(z.string()).optional()
  }),
  marketInsights: z
    .object({
      marketDemand: z.object({
        overall: z.string(),
        byCountry: z.record(z.string()),
        reasoning: z.string()
      }),
      industryTrends: z.array(z.string()).optional(),
      seasonalFactors: z.array(z.string()).optional(),
      regulatoryChanges: z.array(z.string()).optional()
    })
    .optional(),
  competitorAnalysis: z
    .object({
      competitorLandscape: z.string().optional(),
      pricingComparison: z.string().optional(),
      ourUniqueAdvantages: z.array(z.string()).optional(),
      marketPosition: z.string().optional()
    })
    .optional(),
  riskFactors: z
    .array(
      z.object({
        risk: z.string(),
        severity: z.enum(["High", "Medium", "Low"]).default("Medium"),
        mitigation: z.string().optional(),
        impactOnTimeline: z.string().optional()
      })
    )
    .optional(),
  opportunityScore: z
    .object({
      overall: z.number().min(0).max(100),
      breakdown: z.record(z.number()).optional(),
      reasoning: z.string().optional()
    })
    .optional(),
  recommendedStrategy: z
    .object({
      approachType: z.string().optional(),
      keyFocusAreas: z.array(z.string()).optional(),
      pricingStrategy: z.string().optional(),
      timelineStrategy: z.string().optional(),
      conversionTactics: z.array(z.string()).optional(),
      expectedChallenges: z.array(z.string()).optional(),
      successFactors: z.array(z.string()).optional(),
      nextBestAction: z.string().optional()
    })
    .optional(),
  priorityScore: z
    .object({
      salesPriority: z.string().optional(),
      reasoning: z.string().optional(),
      recommendedFollowUp: z.string().optional()
    })
    .optional()
});

/**
 * AI Service powered by Google Gemini 2.5 Flash for CV analysis, sales playbooks,
 * consultation chat, and analytics workflows used across admin surfaces.
 */
export class AzureAIService {
  constructor() {
    const apiKey = getEnvValue('REACT_APP_GEMINI_API_KEY', 'GEMINI_API_KEY');
    const model = getEnvValue('REACT_APP_GEMINI_MODEL', 'GEMINI_MODEL') || 'gemini-2.5-flash';
    const baseUrl = getEnvValue('REACT_APP_GEMINI_BASE_URL', 'GEMINI_BASE_URL');

    if (!apiKey) {
      throw new Error(
        "Missing Gemini credentials. Please configure `GEMINI_API_KEY` (or `REACT_APP_GEMINI_API_KEY`) and redeploy."
      );
    }

    const adapter = new GeminiChatCompletionsAdapter({
      apiKey,
      model,
      baseUrl
    });

    this.chatAdapter = adapter;
    this.client = {
      chat: {
        completions: {
          create: (params) => adapter.create(params)
        }
      }
    };

    this.modelName = adapter.getModelName();
  }

  /**
   * Analyze CV content and extract comprehensive information for immigration scoring
   * @param {Object} userProfile - User's profile from wizard steps
   * @param {string} cvText - Extracted text from CV
   * @returns {Object} - Detailed CV analysis results
   */
  async analyzeCVForImmigration(userProfile, cvText, retries = 1) {
    try {
      const analysisPrompt = this.buildCVAnalysisPrompt(userProfile, cvText);

      const requestParams = {
        messages: [
          {
            role: "system",
            content: "You are an immigration case analyst for Ravana Migration Services. Analyze CVs for Germany, UK, and Canada pathways. Return ONLY valid JSON with extracted information. Mark unknown data as 'Unknown'. Be concise."
          },
          {
            role: "user",
            content: analysisPrompt
          }
        ],
        model: this.modelName,
        max_completion_tokens: 3000  // Reduced from 6000 for faster response
      };

      // Only add temperature if model supports it
      if (this.getModelSupportsTemperature()) {
        requestParams.temperature = 0.2;
      }

      const response = await this.client.chat.completions.create(requestParams);

      const aiResponse = response.choices[0]?.message?.content;

      // Check if response is empty or invalid
      if (!aiResponse || typeof aiResponse !== 'string' || aiResponse.trim().length === 0) {
        console.warn(`Empty or invalid AI response for CV analysis (${retries} retries left)`);

        // Only retry once
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          return this.analyzeCVForImmigration(userProfile, cvText, retries - 1);
        }

        // Fall back to simple analysis if retry exhausted
        console.warn('Using fallback CV analysis due to empty AI response');
        return this.buildFallbackCVAnalysis(userProfile, cvText);
      }

      return this.parseCVAnalysisResponse(aiResponse);

    } catch (error) {
      console.error('Error analyzing CV with Gemini AI:', error);

      // Only retry once on transient errors
      if (retries > 0 && error.status !== 401 && error.status !== 403 && error.status !== 400) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.analyzeCVForImmigration(userProfile, cvText, retries - 1);
      }

      // Use fallback instead of throwing
      console.warn('Using fallback CV analysis due to error:', error.message);
      return this.buildFallbackCVAnalysis(userProfile, cvText);
    }
  }

  /**
   * Generate personalized sales playbook for the sales team
   * @param {Object} userProfile - Complete user profile
   * @param {Object} cvAnalysis - CV analysis results
   * @param {Object} scoringResults - Immigration pathway scoring
   * @returns {Object} - Comprehensive sales playbook
   */
  async generateSalesPlaybook(userProfile, cvAnalysis, scoringResults) {
    try {
      const playbookPrompt = this.buildSalesPlaybookPrompt(userProfile, cvAnalysis, scoringResults);

      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are the lead revenue strategist for Ravana Migration Services. Build deterministic, data-backed sales playbooks for Germany (Study 1,190,000 LKR, Chancenkarte 1,390,000 LKR), UK (400,000 LKR), and Canada (1,150,000 LKR with 25/75 loan split). CRITICAL: Germany English-taught pathways require ONLY IELTS 6.0-6.5—German language is optional. Use ONLY the evidence supplied in the payload; never invent institutions, numbers, or milestones. Every recommendation must reference measurable facts (scores, years of experience, pricing tokens, objection tokens). Output MUST be valid JSON in the exact structure requested—no markdown or additional narration."
          },
          {
            role: "user",
            content: playbookPrompt
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.5 : 1,
        max_completion_tokens: 4000
      });

      const aiResponse = response.choices[0].message.content;
      const fallbackPlaybook = this.buildFallbackSalesPlaybook(userProfile, scoringResults);
      const parsed = this.safeJsonParse(aiResponse, () => fallbackPlaybook, 'sales playbook');
      return this.validateSalesPlaybook(parsed, fallbackPlaybook);

    } catch (error) {
      console.error('Error generating sales playbook:', error);
      throw new Error('Sales playbook generation failed: ' + error.message);
    }
  }

  /**
   * Translate sales playbook to Tamil in ONE API call
   * @param {Object} playbook - English sales playbook
   * @returns {Object} - Tamil translated playbook
   */
  async translatePlaybookToTamil(playbook) {
    try {
      this.debug('🔄 Starting Tamil translation (single API call)...');

      // Create translation prompt with entire playbook
      const translationPrompt = `
        Translate the following sales playbook from English to Tamil.

        CRITICAL INSTRUCTIONS:
        - Keep ALL proper nouns in English (names, countries, visa types, company names)
        - Keep ALL numbers, percentages, currencies in English (e.g., "85%", "LKR 1,190,000", "IELTS 6.0")
        - Keep technical terms in English (Blue Card, Chancenkarte, Express Entry, PGWP, etc.)
        - Keep university names, program names in English
        - Translate ONLY the descriptive text and explanations to Tamil
        - Return ONLY valid JSON with the same structure, no markdown formatting

        ORIGINAL PLAYBOOK (JSON):
        ${JSON.stringify(playbook, null, 2)}

        Return the translated playbook in the EXACT same JSON structure.
      `;

      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are a professional Tamil translator specializing in immigration and business content. Translate to Tamil while preserving all proper nouns, technical terms, numbers, and JSON structure. Return ONLY valid JSON."
          },
          {
            role: "user",
            content: translationPrompt
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.2 : 1,
        max_completion_tokens: 8000 // Increased for full playbook translation
      });

      const aiResponse = response.choices[0].message.content;

      // Clean the response - remove markdown code blocks if present
      let cleanedResponse = aiResponse.trim();
      if (cleanedResponse.startsWith('```json')) {
        cleanedResponse = cleanedResponse.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
      } else if (cleanedResponse.startsWith('```')) {
        cleanedResponse = cleanedResponse.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
      }
      cleanedResponse = cleanedResponse.trim();

      const tamilPlaybook = this.safeJsonParse(cleanedResponse, () => playbook, 'Tamil playbook translation');

      this.debug('✅ Tamil translation complete (single API call)');
      return tamilPlaybook;

    } catch (error) {
      console.error('Error translating playbook to Tamil:', error);

      // Fallback: If single-call fails, return original playbook with error note
      console.warn('⚠️ Translation failed, returning original playbook');
      throw new Error('Tamil translation failed: ' + error.message);
    }
  }

  /**
   * Translate analytics to Tamil in ONE API call
   * @param {Object} analytics - English analytics object
   * @returns {Object} - Tamil translated analytics
   */
  async translateAnalyticsToTamil(analytics) {
    try {
      this.debug('🔄 Starting Analytics Tamil translation (single API call)...');

      const translationPrompt = `
        Translate the following business analytics from English to Tamil.

        CRITICAL INSTRUCTIONS:
        - Keep ALL proper nouns in English (names, countries, visa types, company names)
        - Keep ALL numbers, percentages, currencies in English (e.g., "85%", "LKR 1,190,000", "70-100%")
        - Keep technical terms in English (Blue Card, Chancenkarte, Express Entry, Hot/Warm/Cold lead, etc.)
        - Translate ONLY the descriptive text and explanations to Tamil
        - Return ONLY valid JSON with the same structure, no markdown formatting

        ORIGINAL ANALYTICS (JSON):
        ${JSON.stringify(analytics, null, 2)}

        Return the translated analytics in the EXACT same JSON structure.
      `;

      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are a professional Tamil translator specializing in business and sales content. Translate to Tamil while preserving all proper nouns, technical terms, numbers, and JSON structure. Return ONLY valid JSON."
          },
          {
            role: "user",
            content: translationPrompt
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.2 : 1,
        max_completion_tokens: 6000
      });

      const aiResponse = response.choices[0].message.content;

      // Clean the response
      let cleanedResponse = aiResponse.trim();
      if (cleanedResponse.startsWith('```json')) {
        cleanedResponse = cleanedResponse.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
      } else if (cleanedResponse.startsWith('```')) {
        cleanedResponse = cleanedResponse.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
      }
      cleanedResponse = cleanedResponse.trim();

      const tamilAnalytics = this.safeJsonParse(cleanedResponse, () => analytics, 'Tamil analytics translation');

      this.debug('✅ Analytics Tamil translation complete (single API call)');
      return tamilAnalytics;

    } catch (error) {
      console.error('Error translating analytics to Tamil:', error);
      throw new Error('Analytics Tamil translation failed: ' + error.message);
    }
  }

  /**
   * Translate a single text to target language with retry logic
   * @param {string} text - Text to translate
   * @param {string} targetLanguage - Target language
   * @param {number} retries - Number of retries remaining
   * @returns {string} - Translated text
   */
  async translateText(text, targetLanguage, retries = 3) {
    try {
      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: `You are a professional translator. Translate to ${targetLanguage}. Keep proper nouns, country names, visa types, and numbers in English.`
          },
          {
            role: "user",
            content: `Translate to ${targetLanguage}: ${text}`
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.3 : 1,
        max_completion_tokens: 1000
      });

      return response.choices[0].message.content.trim();
    } catch (error) {
      console.error(`Error translating to ${targetLanguage}:`, error);

      // If rate limit error and retries remaining, wait and retry with exponential backoff
      if (error.status === 429 && retries > 0) {
        const waitTime = (4 - retries) * 3000; // 3s, 6s, 9s backoff
        this.debug(`⏳ Rate limited. Waiting ${waitTime/1000}s before retry (${retries} retries left)...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return this.translateText(text, targetLanguage, retries - 1);
      }

      return text; // Fallback to original text if all retries exhausted
    }
  }

  /**
   * Generate AI-powered advanced analytics for lead
   * @param {Object} userProfile - User profile data
   * @param {Object} cvAnalysis - CV analysis results
   * @param {Object} scoringResults - Pathway scoring results
   * @returns {Object} - Advanced analytics insights
   */
  async generateAdvancedAnalytics(userProfile, cvAnalysis, scoringResults) {
    try {
      const analyticsPrompt = this.buildAnalyticsPrompt(userProfile, cvAnalysis, scoringResults);

      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are the senior analytics lead for Ravana Migration Services. Produce deterministic, audit-ready lead analytics for Germany (Study 1,190,000 LKR, Chancenkarte 1,390,000 LKR), UK (400,000 LKR), and Canada (1,150,000 LKR with 25/75 loan facility). Reminder: Germany English-taught pathways require ONLY IELTS 6.0-6.5—German is optional. Use ONLY payload facts plus deterministic risk flags—no speculation or hallucination. Quantify every probability, amount, or timeframe; keep numbers as numeric JSON values. Output MUST be valid JSON conforming to the schema in the user prompt—no markdown, prose, or trailing commentary."
          },
          {
            role: "user",
            content: analyticsPrompt
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.4 : 1,
        max_completion_tokens: 4000
      });

      const aiResponse = response.choices[0].message.content;
      const analytics = this.safeJsonParse(
        aiResponse,
        () => this.buildFallbackAnalytics(userProfile, scoringResults),
        'business analytics'
      );

      return this.validateAnalyticsResponse(analytics, userProfile, scoringResults);

    } catch (error) {
      console.error('Error generating advanced analytics:', error);
      return this.buildFallbackAnalytics(userProfile, scoringResults);
    }
  }

  /**
   * Extract and analyze information from CV file using AI
   * @param {File} cvFile - CV file (PDF, DOC, DOCX)
   * @param {string} cvUrl - Optional public URL to CV file
   * @returns {Object} - Extracted CV information
   */
  async extractCVInformation(cvFile, cvUrl = null) {
    try {
      this.debug('📄 Extracting text from PDF file...');

      // Use PDF.js to extract actual text from PDF
      let pdfText = '';
      try {
        pdfText = await this.extractTextFromPDF(cvFile);
      } catch (pdfErr) {
        console.error('Error extracting PDF text:', pdfErr);
        // Fall back to lightweight extraction for contacts from raw bytes/filename
        const fallback = await this.basicExtractFromFile(cvFile);
        return fallback;
      }
      this.debug('📝 Extracted PDF text length:', pdfText.length, 'characters');
      this.debug('📝 FULL CV TEXT BEING SENT TO AI:\n', pdfText);
      this.debug('📝 End of full CV text');

      const extractionPrompt = `
        You are analyzing a CV/Resume. Below is the COMPLETE text extracted from the PDF.

        ${cvUrl ? `CV URL: ${cvUrl}\n\n` : ''}

        COMPLETE CV TEXT:
        ${pdfText}

        INSTRUCTIONS:
        - Extract ALL education entries (degrees, institutions, years)
        - Extract ALL work experience entries (companies, titles, durations, responsibilities)
        - Extract ALL technical skills, certifications, and projects mentioned
        - Be thorough - don't skip any information
        - Calculate total years of experience accurately

        Return a JSON object with this EXACT structure:
        {
          "personalInfo": {
            "name": "Full name from CV",
            "email": "Email address if present",
            "phone": "Phone number if present",
            "location": "Current location if present"
          },
          "education": [
            {
              "degree": "Degree name",
              "field": "Field of study",
              "institution": "University name",
              "year": "Graduation year or range",
              "gpa": "GPA if mentioned"
            }
          ],
          "experience": [
            {
              "title": "Job title",
              "company": "Company name",
              "duration": "Duration",
              "responsibilities": ["Responsibilities"],
              "skills": ["Skills used"]
            }
          ],
          "skills": {
            "technical": ["Technical skills"],
            "soft": ["Soft skills"],
            "languages": [{"language": "Language", "proficiency": "Level"}]
          },
          "certifications": ["Certifications"],
          "projects": ["Projects"],
          "totalExperienceYears": "Total years"
        }

        Return ONLY the JSON object, no markdown formatting.
      `;

      // Heuristic contacts from raw text
      const contactHeuristics = this.extractContactsFromText(pdfText, cvFile?.name);
      const structHeuristics = this.extractStructuredFromText(pdfText);

      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are an expert CV parser. Extract ALL information accurately from the provided CV text and return ONLY valid JSON with no markdown formatting, no code blocks, no additional text. Just pure JSON."
          },
          {
            role: "user",
            content: extractionPrompt
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.1 : 1,
        max_completion_tokens: 2000
      });

      const aiResponse = response.choices[0].message.content || '';
      const extracted = this.safeJsonParse(
        aiResponse,
        () => ({ personalInfo: {}, education: [], experience: [], skills: {}, certifications: [], projects: [], totalExperienceYears: null }),
        'cv extraction'
      );

      // Merge heuristic contacts if missing from AI output
      try {
        const merged = { ...extracted, personalInfo: { ...(extracted.personalInfo || {}) } };
        if (contactHeuristics.email && !merged.personalInfo.email) merged.personalInfo.email = contactHeuristics.email;
        if (contactHeuristics.phone && !merged.personalInfo.phone) merged.personalInfo.phone = contactHeuristics.phone;
        if (contactHeuristics.name && !merged.personalInfo.name) merged.personalInfo.name = contactHeuristics.name;
        if (contactHeuristics.location && !merged.personalInfo.location) merged.personalInfo.location = contactHeuristics.location;
        if (contactHeuristics.nationality && !merged.personalInfo.nationality) merged.personalInfo.nationality = contactHeuristics.nationality;
        // Merge structured education/certifications heuristics if AI returned empty
        if ((Array.isArray(merged.education) ? merged.education.length : 0) === 0 && Array.isArray(structHeuristics.education)) {
          merged.education = structHeuristics.education;
        }
        if ((Array.isArray(merged.certifications) ? merged.certifications.length : 0) === 0 && Array.isArray(structHeuristics.certifications)) {
          merged.certifications = structHeuristics.certifications;
        }
        this.debug('✅ Extracted CV data:', merged);
        return merged;
      } catch (_) {
        this.debug('✅ Extracted CV data:', extracted);
        return extracted;
      }

    } catch (error) {
      console.error('Error extracting CV information:', error);
      // Final fallback to minimal extraction
      try {
        const fallback = await this.basicExtractFromFile(cvFile);
        return fallback;
      } catch (e) {
        throw new Error('CV extraction failed: ' + error.message);
      }
    }
  }

  /**
   * Extract text from PDF using PDF.js
   */
  async extractTextFromPDF(file) {
    try {
      // Import PDF.js dynamically
      const pdfjsLib = await import('pdfjs-dist');
      try {
        const ver = pdfjsLib.version || '4.0.269';
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${ver}/build/pdf.worker.min.mjs`;
      } catch (_) {}

      // Convert file to array buffer
      const arrayBuffer = await file.arrayBuffer();

      // Load PDF
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let fullText = '';

      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n\n';
      }

      return fullText.trim();
    } catch (error) {
      console.error('Error extracting PDF text:', error);
      throw new Error('PDF text extraction failed: ' + error.message);
    }
  }

  /**
   * Heuristic contact extraction from raw text + filename
   */
  extractContactsFromText(text, fileName) {
    try {
      const content = (text || '').toString();
      const emailMatch = content.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
      // Prefer international formats with + and spaces/dots
      const phoneMatch = content.match(/\+?\d[\d\s().-]{7,}/);
      let name = '';
      // Try to find a name-like line at the top: two capitalized words
      const firstLines = content.split(/\n|\r/).slice(0, 5).join(' ');
      const nameLine = firstLines.match(/\b([A-Z][a-zA-Z']+\s+[A-Z][a-zA-Z']+(?:\s+[A-Z][a-zA-Z']+)*)\b/);
      if (nameLine) {
        name = nameLine[1];
      }
      if (fileName) {
        const base = fileName.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\b(cv|resume)\b/ig, '').trim();
        const tokens = base.split(/\s+/).filter(Boolean);
        const upperTokens = tokens.filter(t => /^[A-Z][A-Z'.-]+$/.test(t));
        if (upperTokens.length >= 2) {
          // Prefer filename all-caps names (e.g., THUJITHA VIMALINI PETHURU)
          name = upperTokens.join(' ');
        } else if (!name && tokens.length >= 2) {
          name = tokens.join(' ');
        }
      }
      // Nationality and country
      const nationalityMatch = content.match(/Nationality\s*[:\-]?\s*([A-Za-z\s]+)/i);
      const countryMention = content.match(/\bSri\s*Lanka\b/i);
      const location = countryMention ? 'Sri Lanka' : undefined;
      const nationality = nationalityMatch ? nationalityMatch[1].trim() : undefined;
      return {
        email: emailMatch ? emailMatch[0] : undefined,
        phone: phoneMatch ? phoneMatch[0] : undefined,
        name: name || undefined,
        location,
        nationality
      };
    } catch (_) {
      return { email: undefined, phone: undefined, name: undefined, location: undefined, nationality: undefined };
    }
  }

  /**
   * Heuristic structural extraction for certifications/education from raw text
   */
  extractStructuredFromText(text) {
    try {
      const content = (text || '').toString().replace(/\s+/g, ' ').trim();
      const items = [];
      const certifications = [];
      const education = [];
      const monthRegex = '(January|February|March|April|May|June|July|August|September|October|November|December)';
      const dateRange = new RegExp(`${monthRegex} \d{4}\s*-\s*${monthRegex} \d{4}`, 'i');
      // Pattern: Title, Organization (Grade) Month YYYY - Month YYYY
      const pattern = /([A-Za-z][A-Za-z\s&.'-]+?),\s*([A-Za-z][A-Za-z\s&().'-]+?)\s*\(([^)]+)\)\s+([A-Za-z]+ \d{4})\s*-\s*([A-Za-z]+ \d{4})/g;
      let m;
      while ((m = pattern.exec(content)) !== null) {
        const title = m[1].trim();
        const org = m[2].trim();
        const grade = m[3].trim();
        const start = m[4].trim();
        const end = m[5].trim();
        const entry = { name: title, organization: org, result: grade, period: `${start} - ${end}` };
        // Heuristic: map to education if looks like formal program, else certifications
        if (/education|diploma|degree|pre\s*-?\s*school|primary|certificate/i.test(title)) {
          education.push({ degree: title, field: undefined, institution: org, year: end, gpa: grade });
        } else {
          certifications.push(entry);
        }
      }
      return { certifications, education };
    } catch (_) {
      return { certifications: [], education: [] };
    }
  }

  /**
   * Fallback: extract basic contacts and name from file when PDF.js fails.
   */
  async basicExtractFromFile(file) {
    try {
      let text = '';
      try {
        if (typeof file.text === 'function') {
          text = await file.text();
        } else {
          const buf = await file.arrayBuffer();
          text = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(buf));
        }
      } catch (_) {
        // ignore
      }

      const content = text || '';
      const emailMatch = content.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
      const phoneMatch = content.match(/\+?\d[\d\s().-]{7,}/);

      // Derive name from filename as last resort
      const baseName = (file.name || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\b(cv|resume)\b/ig, '').trim();
      const nameTokens = baseName.split(/\s+/).filter(Boolean);
      let name = '';
      if (nameTokens.length >= 2) {
        name = nameTokens.join(' ');
      }

      return {
        personalInfo: {
          name: name || undefined,
          email: emailMatch ? emailMatch[0] : undefined,
          phone: phoneMatch ? phoneMatch[0] : undefined,
          location: undefined
        },
        education: [],
        experience: [],
        skills: {},
        certifications: [],
        projects: [],
        totalExperienceYears: undefined
      };
    } catch (e) {
      console.error('basicExtractFromFile failed:', e);
      return { personalInfo: {}, education: [], experience: [], skills: {}, certifications: [], projects: [], totalExperienceYears: undefined };
    }
  }

  /**
   * Build comprehensive CV analysis prompt for detailed immigration assessment
   */
  buildCVAnalysisPrompt(userProfile, cvText) {
    const targetCountry = userProfile.countrySelection?.selectedCountry || 'Germany';
    const selectedPathway = userProfile.reviewData?.selectedPathway || 'Not specified';

    return `
      As a senior immigration consultant at Ravana Migration Services, provide a COMPREHENSIVE, DETAILED analysis of this candidate's CV focusing on ${targetCountry} immigration eligibility.

      🎯 CANDIDATE'S WIZARD SELECTIONS (PRIMARY FOCUS):
      - Selected Country: ${targetCountry}
      - Selected Pathway: ${selectedPathway}
      - Immigration Purpose: ${userProfile.countrySelection?.answers?.purpose || userProfile.countrySelection?.answers?.immigrationPurpose || 'Not specified'}

      ⚠️ CRITICAL INSTRUCTION: Base your entire analysis on the candidate's selected country (${targetCountry}) and pathway (${selectedPathway}). This is their PRIMARY interest. You may suggest alternative pathways ONLY as secondary options if they significantly improve success chances.

      RAVANA'S ${targetCountry.toUpperCase()} SERVICES:
      ${targetCountry === 'Germany' || targetCountry === 'germany' ? `
      - Bachelor/Master Study: LKR 1,190,000 (FREE tuition, blocked account €13,080/year, IELTS 6.0-6.5 ONLY for English programs)
      - Chancenkarte/Skilled Worker: LKR 1,390,000 (6-month job seeker visa, IELTS 6.0-6.5 for English jobs)
      - CRITICAL: German language NOT required for English-taught programs!
      ` : targetCountry === 'UK' || targetCountry === 'uk' ? `
      - Foundation/Bachelor/Master: LKR 400,000 (IELTS 5.5-6.0, 2-year Graduate Route work visa)
      ` : targetCountry === 'Canada' || targetCountry === 'canada' ? `
      - Certificate/Diploma: LKR 1,150,000 (IELTS 6.0, LOAN: 25% upfront, 75% after arrival, CAD 398-790/month)
      ` : ''}

      CANDIDATE CONTEXT:
      - Name: ${userProfile.personalInfo?.firstName} ${userProfile.personalInfo?.lastName}
      - PRIMARY Target Country: ${targetCountry}
      - SELECTED Pathway: ${selectedPathway}
      - Wizard Education: ${userProfile.education?.highestEducation} in ${userProfile.education?.fieldOfStudy}
      - Wizard Experience: ${userProfile.experience?.length} positions
      - Language Tests: ${JSON.stringify(userProfile.languageSkills)}
      - Financial Capacity: ${JSON.stringify(userProfile.financialInfo)}
      - Age: ${userProfile.personalInfo?.age || 'Not specified'}

      ACTUAL CV CONTENT:
      ${JSON.stringify(cvText, null, 2)}

      INSTRUCTIONS:
      Analyze the CV content THOROUGHLY and extract EVERY relevant detail. Don't just summarize - provide specific, actionable insights for immigration assessment.

      Return a DETAILED JSON analysis:
      {
        "educationAssessment": {
          "level": "Exact degree name and level (e.g., Bachelor of Science in Computer Science)",
          "institutions": ["List ALL educational institutions with years attended"],
          "fields": ["All fields of study mentioned"],
          "recognition": "Detailed assessment of credential recognition in ${targetCountry} - mention WES, Anabin, or other evaluation services",
          "relevance": "How relevant is education to in-demand occupations in ${targetCountry}",
          "institutionQuality": "Reputation of institutions (research if known universities)",
          "gpaStrength": "GPA or grades if mentioned, compare to ${targetCountry} standards",
          "additionalEducation": ["Any additional courses, bootcamps, certificates from CV"]
        },
        "experienceAnalysis": {
          "positions": [
            {
              "title": "Exact job title from CV",
              "company": "Company name",
              "duration": "Start date - End date (calculate months/years)",
              "responsibilities": ["Key responsibilities that demonstrate skills"],
              "achievements": ["Quantifiable achievements"],
              "relevance": "How this role aligns with ${targetCountry} labor market needs"
            }
          ],
          "totalYears": "Calculate exact total professional experience in years and months",
          "relevantExperience": "Years of experience relevant to target occupation",
          "careerProgression": "Analysis of career growth trajectory",
          "industryDemand": "Detailed assessment of industry demand in ${targetCountry} - be specific",
          "seniority": "Detailed seniority level with justification",
          "companyReputation": "Analysis of company sizes, types (startup/MNC/enterprise)",
          "employmentGaps": "Note any employment gaps and their duration",
          "currentSalary": "If mentioned, current or last salary",
          "leadershipExperience": "Any team leadership, management, or project lead roles"
        },
        "skillsEvaluation": {
          "technicalSkills": {
            "programming": ["Specific programming languages with proficiency if stated"],
            "frameworks": ["Frameworks, libraries, tools"],
            "software": ["Software applications, platforms"],
            "specializations": ["Domain-specific expertise"],
            "inDemandSkills": ["Which skills are particularly valuable for ${targetCountry} immigration"]
          },
          "languageSkills": {
            "english": "Proficiency level + test scores if mentioned (IELTS/TOEFL/etc)",
            "german": "Proficiency level + test scores if mentioned (Goethe/TestDaF/etc) - NOTE: For Germany English-taught programs, German is NOT required",
            "otherLanguages": ["Any other languages with proficiency levels"],
            "languageAdvantage": "How language skills benefit ${targetCountry} immigration - IMPORTANT: For Germany, IELTS 6.0-6.5 alone qualifies for English-taught Bachelor/Master programs (no German needed)"
          },
          "softSkills": ["Extract all soft skills mentioned (communication, leadership, problem-solving, etc)"],
          "certifications": [
            {
              "name": "Certification name",
              "issuer": "Issuing organization",
              "year": "Year obtained",
              "relevance": "Relevance to ${targetCountry} job market"
            }
          ],
          "professionalMemberships": ["Any professional associations or memberships"]
        },
        "projectsAndAchievements": {
          "notableProjects": [
            {
              "name": "Project name",
              "description": "What the project was about",
              "technologies": ["Technologies used"],
              "impact": "Business impact or results"
            }
          ],
          "awards": ["Any awards, recognitions, or honors"],
          "publications": ["Research papers, articles, patents"],
          "speakingEngagements": ["Conferences, presentations, workshops"]
        },
        "immigrationStrengths": [
          "List 10-15 SPECIFIC strengths for ${targetCountry} immigration",
          "Include: years of experience, education level, in-demand skills, language abilities",
          "Reference specific visa requirements (Blue Card qualifications, Express Entry points, etc)",
          "Highlight any unique advantages (rare skills, high-demand occupation, salary level)"
        ],
        "potentialConcerns": [
          "List ALL potential concerns or weaknesses",
          "Include: credential recognition issues, language gaps, employment gaps",
          "Missing documents or information that will be needed",
          "Skills or qualifications that may need upgrading"
        ],
        "selectedPathwayAnalysis": {
          "pathway": "${selectedPathway}",
          "country": "${targetCountry}",
          "eligibilityScore": "0-100 score for THIS SPECIFIC pathway (calculate based on requirements)",
          "scoreBreakdown": {
            "education": "0-25 points - How well education matches pathway requirements",
            "experience": "0-25 points - Years and relevance of experience for this pathway",
            "language": "0-25 points - Language proficiency for this pathway (IELTS for ${targetCountry})",
            "financialReadiness": "0-15 points - Budget alignment with pathway costs",
            "ageOptimality": "0-10 points - Age advantage/disadvantage for this pathway"
          },
          "eligible": "Excellent/Good/Fair/Poor - Overall eligibility for ${selectedPathway} in ${targetCountry}",
          "strengths": ["List 5-8 specific strengths for THIS pathway"],
          "weaknesses": ["List 3-5 specific weaknesses for THIS pathway"],
          "missingRequirements": ["Exact requirements missing for THIS pathway"],
          "timelineEstimate": "Realistic processing time for THIS pathway (be specific: X-Y months)",
          "successProbability": "0-100% - Realistic chance of success for THIS pathway",
          "improvementActions": ["Specific actions to improve eligibility for THIS pathway"]
        },
        "alternativePathways": [
          {
            "pathway": "Alternative pathway name (ONLY if significantly better than selected)",
            "country": "Country (can suggest different country if much better fit)",
            "eligibilityScore": "0-100 score",
            "reasoning": "Why this is better than selected pathway ${selectedPathway}",
            "timeline": "Expected processing time",
            "advantageOverSelected": "Specific advantages over ${selectedPathway}"
          }
        ],
        "salaryExpectations": {
          "currentSalary": "If mentioned in CV",
          "targetCountrySalary": "Expected salary range in ${targetCountry} for this profile",
          "competitiveness": "How competitive is this profile in target job market"
        },
        "missingCriticalInfo": [
          "List specific information missing from CV that's critical for immigration",
          "Include: exact dates, credential evaluations needed, language test scores needed, etc"
        ],
        "overallAssessment": "Provide 3-4 paragraphs of detailed assessment covering: (1) Specific viability for ${selectedPathway} in ${targetCountry}, (2) Strongest selling points for THIS pathway, (3) Main challenges and how to overcome them, (4) Step-by-step recommended strategy, (5) Expected timeline with milestones, (6) Realistic success probability percentage with reasoning"
      }

      CRITICAL SCORING INSTRUCTIONS FOR ${selectedPathway} in ${targetCountry}:
      - PRIMARY FOCUS: Score specifically for ${selectedPathway} - this is what the candidate selected
      - Education Score (0-25): ${targetCountry === 'Germany' || targetCountry === 'germany' ? 'Bachelor=20, Master=25, PhD=25, A/L 3 passes=15 for study programs' : targetCountry === 'UK' || targetCountry === 'uk' ? 'Master=25, Bachelor=22, Foundation eligible=15, O/L for Foundation=10' : 'Recognized degree=25, Diploma=20, Certificate eligible=15'}
      - Experience Score (0-25): ${selectedPathway?.toLowerCase().includes('study') || selectedPathway?.toLowerCase().includes('bachelor') || selectedPathway?.toLowerCase().includes('master') ? '0-2 years=10, 2-5 years=15, 5+ years=20 (study pathways value experience but it\'s not primary)' : '0-2 years=5, 2-5 years=15, 5+ years=25 (work visas require strong experience)'}
      - Language Score (0-25): IELTS 6.5+=25, 6.0-6.4=20, 5.5-5.9=15, 5.0-5.4=10, <5.0=5 (For ${targetCountry} ${selectedPathway})
      - Financial Score (0-15): Budget covers total costs=15, Covers 75%=12, Covers 50%=8, <50%=5
      - Age Score (0-10): 18-25=10, 26-30=9, 31-35=7, 36-40=5, 41+=3
      - TOTAL: Sum all scores to get final eligibility score (0-100)

      IMPORTANT:
      - PRIORITIZE the selected pathway (${selectedPathway}) - analyze in extreme detail
      - Suggest alternatives ONLY if they are significantly better (20+ points higher score)
      - Extract EVERY detail from CV - be thorough, not generic
      - Provide pathway-specific insights based on actual ${targetCountry} requirements
      - Make scoring realistic and accurate for ${selectedPathway}
      - Return ONLY valid JSON, no markdown formatting
    `;
  }

  /**
   * Build sales playbook generation prompt
   */
  buildSalesPlaybookPrompt(userProfile, cvAnalysis = {}, scoringResults = {}) {
    const context = this.buildPlaybookContext(userProfile, cvAnalysis || {}, scoringResults || {});
    const {
      leadProfile,
      scoring,
      cvHighlights,
      strengths,
      objections,
      riskSignals,
      recommendedAlternatives,
      staticTokens
    } = context;

    const payload = {
      leadProfile,
      scoring,
      cvHighlights,
      strengths,
      objections,
      riskSignals,
      recommendedAlternatives
    };

    const staticTokenSection = this.buildStaticTokenSection(
      staticTokens.pricing,
      staticTokens.objections
    );

    return `
You are a senior immigration sales strategist at Ravana Migration Services. Build a deterministic, conversion-focused sales playbook that a sales rep can execute immediately.

${staticTokenSection}

Use the JSON payload below. It is pre-trimmed and normalized—do not ask for more data. Base every recommendation on these facts.

PAYLOAD:
${JSON.stringify(payload, null, 2)}

STRICT OUTPUT REQUIREMENTS:
- Return ONLY valid JSON. No markdown. No commentary.
- Reference the pricing token ${staticTokens.pricing.token} when positioning fees.
- When responding to objections, cite the matching objection token (e.g., ${staticTokens.objections[0].token}).
- Leverage riskSignals to pre-empt objections and strengths to build urgency and confidence.

OUTPUT SHAPE:
{
  "recommendedApproach": "2-3 persuasive paragraphs tailored to this lead. Reference their education, experience, key strengths, scoring data, and pricing token ${staticTokens.pricing.token}.",
  "keyTalkingPoints": ["5-6 bullet points referencing concrete data or strengths"],
  "objectionHandling": [
    {
      "token": "OBJ_BUDGET_SUPPORT",
      "objection": "Short statement",
      "response": "Detailed rebuttal referencing pricing token and lead data",
      "evidence": ["Data points from payload that support the response"]
    }
  ],
  "nextSteps": ["Chronological list of 5-7 concrete actions with timing"],
  "personalizedMessaging": {
    "openingLine": "First sentence to use on call/email",
    "urgencyCue": "How to create urgency grounded in scoring/timelines",
    "followUpAngle": "Angle to use if lead goes quiet"
  }
}

Rules:
- Do not invent data. Quote directly from the payload (institutions, years, scores).
- Keep tone consultative and confident.
- Every objection entry must include a valid token and at least one evidence reference.
`;

  }

  /**
   * Format technical skills object into readable string
   */
  formatTechnicalSkills(technicalSkills) {
    if (!technicalSkills) return 'Various technical skills';

    // If it's already an array, join it
    if (Array.isArray(technicalSkills)) {
      return technicalSkills.join(', ');
    }

    // If it's an object with nested skills, format them
    const skillParts = [];
    if (technicalSkills.programming?.length) {
      skillParts.push(`Programming: ${technicalSkills.programming.join(', ')}`);
    }
    if (technicalSkills.frameworks?.length) {
      skillParts.push(`Frameworks: ${technicalSkills.frameworks.join(', ')}`);
    }
    if (technicalSkills.inDemandSkills?.length) {
      skillParts.push(`In-demand: ${technicalSkills.inDemandSkills.join(', ')}`);
    }

    return skillParts.length > 0 ? skillParts.join(' | ') : 'Various technical skills';
  }

  escapeString(str) {
    if (typeof str !== 'string') return str;
    return str
      .replace(/\\/g, '\\')
      .replace(/"/g, '\"')
      .replace(/\//g, '\/')
      .replace(/\b/g, '\b')
      .replace(/\f/g, '\f')
      .replace(/\n/g, '\n')
      .replace(/\r/g, '\r')
      .replace(/\t/g, '\t');
  }

  /**
   * Parse CV analysis response from AI
   */
  parseCVAnalysisResponse(aiResponse) {
    try {
      // Clean the response - remove markdown code blocks if present
      let cleanedResponse = (aiResponse || '').toString().trim();

      // Strip control characters that break JSON
      cleanedResponse = cleanedResponse.replace(/[\u0000-\u001F\u007F]/g, ' ');

      // Remove ```json and ``` markers
      if (cleanedResponse.startsWith('```json')) {
        cleanedResponse = cleanedResponse.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
      } else if (cleanedResponse.startsWith('```')) {
        cleanedResponse = cleanedResponse.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
      }

      cleanedResponse = cleanedResponse.trim();

      // Normalize smart quotes
      cleanedResponse = cleanedResponse
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2018\u2019]/g, "'");

      // Remove trailing commas before closing braces/brackets
      cleanedResponse = cleanedResponse.replace(/,(\s*[}\]])/g, '$1');

      // Try to parse cleaned response first (do NOT escape JSON string globally)
      try {
        return JSON.parse(cleanedResponse);
      } catch (parseError) {
        console.warn('Initial JSON parse failed, attempting extraction:', parseError.message);

        // Fallback: Extract JSON from the response using regex
        const jsonMatch = cleanedResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            return JSON.parse(jsonMatch[0]);
          } catch (secondError) {
            console.warn('Extracted JSON parse failed, attempting repair:', secondError.message);

            // Try to fix common JSON issues
            let repairedJson = jsonMatch[0]
              .replace(/[\u0000-\u001F\u007F]/g, ' ')
              .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
              .replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3') // Quote unquoted property names
              .replace(/:\s*([a-zA-Z][a-zA-Z0-9_]*)(\s*[,\}])/g, ': "$1"$2') // Quote unquoted string values
              .replace(/[\u201C\u201D]/g, '"')
              .replace(/[\u2018\u2019]/g, "'");

            try {
              return JSON.parse(repairedJson);
            } catch (thirdError) {
              console.error('JSON repair failed:', thirdError.message);
              // One more attempt: balance braces/brackets
              const balanced = repairedJson.replace(/\{([^}]*)$/g, '{$1}').replace(/\[([^]]*)$/g, '[$1]');
              try {
                return JSON.parse(balanced);
              } catch (_) {}
            }
          }
        }
      }

      // Fallback if JSON parsing fails - return minimal valid structure
      console.warn('Using fallback CV analysis structure');
      return this.buildFallbackCVAnalysis({}, cleanedResponse || '');
    } catch (error) {
      console.error('Error parsing CV analysis response:', error);
      return {
        error: "Failed to parse AI response",
        rawResponse: aiResponse
      };
    }
  }

  /**
   * Parse sales playbook response from AI
   */
  parseSalesPlaybookResponse(aiResponse, fallback) {
    const fallbackPlaybook = fallback || this.buildFallbackSalesPlaybook({}, {});
    const parsed = this.safeJsonParse(aiResponse, () => fallbackPlaybook, 'sales playbook');
    return this.validateSalesPlaybook(parsed, fallbackPlaybook);
  }

  /**
   * Convert file to base64 string for AI processing
   * @param {File} file - File to convert
   * @returns {Promise<string>} - Base64 encoded file content
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64 = e.target.result;
        resolve(base64);
      };

      reader.onerror = (e) => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Generate AI-powered conversion timeline for lead conversion journey
   * @param {Object} timelineContext - Context for timeline generation
   * @returns {Array} - Timeline milestones with estimated dates
   */
  async generateConversionTimeline(timelineContext) {
    try {
      this.debug('🕐 Generating conversion timeline with AI...');

      const normalizedContext = this.buildTimelineContext(timelineContext);
      const timelinePrompt = this.buildTimelinePrompt(normalizedContext);

      const response = await this.client.chat.completions.create({
        messages: [
          {
            role: "system",
            content: "You are the lead conversion strategist at Ravana Migration Services. Build deterministic immigration sales timelines grounded ONLY in the provided context and CRM history. Never invent or alter past events. Factor in Ravana's pricing tokens (Germany study 1,190,000 LKR, Chancenkarte 1,390,000 LKR, UK 400,000 LKR, Canada 1,150,000 LKR with loan split 25/75) and our payment cadence (50% upfront, 50% post-offer). Stage titles must be action oriented (≤40 characters). Use ISO 8601 dates (YYYY-MM-DD) and natural language durations such as \"3 business days\". keyActions must contain 1-4 high-impact, verb-led tasks (e.g., \"Send Chancenkarte document checklist\", \"Collect 50% onboarding payment\"). successCriteria must describe observable proof that the milestone is complete. Respect statuses completed/current/pending and align completed items with CRM events. Return only a JSON array – no prose, markdown, or trailing commentary."
          },
          {
            role: "user",
            content: timelinePrompt
          }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.5 : 1,
        max_completion_tokens: 3000
      });

      const aiResponse = response.choices[0].message.content;
      const timeline = this.safeJsonParse(
        aiResponse,
        () => this.buildFallbackTimeline(normalizedContext),
        'conversion timeline'
      );

      return this.validateTimelineResponse(timeline, normalizedContext);

    } catch (error) {
      console.error('Error generating conversion timeline:', error);
      const fallbackContext = this.buildTimelineContext(timelineContext);
      return this.buildFallbackTimeline(fallbackContext);
    }
  }

  /**
   * Generate lead score based on profile and CV analysis
   */
  generateLeadScore(userProfile, cvAnalysis, scoringResults) {
    let score = 0;

    // Base score from pathway matching
    if (scoringResults.bestMatch?.score) {
      score += Math.min(scoringResults.bestMatch.score, 40);
    }

    // Education quality score
    if (cvAnalysis.educationAssessment?.level === 'Masters' || cvAnalysis.educationAssessment?.level === 'PhD') {
      score += 15;
    } else if (cvAnalysis.educationAssessment?.level === 'Bachelors') {
      score += 10;
    }

    // Experience score
    const experienceYears = parseInt(cvAnalysis.experienceAnalysis?.totalYears) || 0;
    if (experienceYears >= 5) score += 15;
    else if (experienceYears >= 3) score += 10;
    else if (experienceYears >= 1) score += 5;

    // Skills score
    if (cvAnalysis.skillsEvaluation?.technicalSkills?.length >= 3) score += 10;
    if (cvAnalysis.skillsEvaluation?.languageSkills?.english === 'Fluent') score += 10;

    // Financial capacity score
    if (userProfile.financialInfo?.annualIncome || userProfile.financialInfo?.educationBudget) {
      score += 10;
    }

    return Math.min(score, 100);
  }

  // ---------------------
  // Helper utilities
  // ---------------------

  debug(message, ...optionalParams) {
    try {
      console.debug(message, ...optionalParams);
    } catch (_) {
      // no-op
    }
  }

  normalizeCountry(country) {
    return (country || '').toString().trim().toLowerCase();
  }

  resolvePricingToken(targetCountry, selectedPathway) {
    const country = this.normalizeCountry(targetCountry);
    const pathway = (selectedPathway || '').toString().trim().toLowerCase();

    if (country === 'germany' || country === 'de') {
      if (pathway.includes('chance') || pathway.includes('worker') || pathway.includes('job')) {
        return PRICING_LIBRARY.GERMANY_CHANCENKARTE;
      }
      return PRICING_LIBRARY.GERMANY_STUDY;
    }

    if (country === 'uk' || country === 'united kingdom') {
      return PRICING_LIBRARY.UK_STUDY;
    }

    if (country === 'canada' || country === 'ca') {
      return PRICING_LIBRARY.CANADA_STUDY;
    }

    return PRICING_LIBRARY.GERMANY_STUDY;
  }

  getObjectionTokens() {
    return Object.values(OBJECTION_LIBRARY);
  }

  preparePromptList(list, limit = PROMPT_LIST_LIMIT) {
    if (!Array.isArray(list)) return [];
    return list
      .map(item => {
        if (item == null) return null;
        if (typeof item === 'string') return item.trim();
        if (typeof item === 'object') {
          try {
            return JSON.stringify(item);
          } catch (error) {
            return null;
          }
        }
        return item.toString();
      })
      .filter(Boolean)
      .slice(0, limit);
  }

  truncate(text, maxLength = 320) {
    if (!text || typeof text !== 'string') return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 3)}...`;
  }

  sanitizeNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,(?=\d{3}(?:\D|$))/g, '').replace(/,/g, '.');
      const parsed = parseFloat(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  formatCurrency(value) {
    const numeric = this.sanitizeNumber(value);
    if (numeric == null) return value || 'Not specified';
    return `LKR ${numeric.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }

  buildStaticTokenSection(pricingToken, objectionTokens) {
    const pricingLine = `${pricingToken.token}: ${pricingToken.label} (${pricingToken.price}; ${pricingToken.paymentStructure})`;
    const objectionLines = objectionTokens
      .map(token => `${token.token}: ${token.summary}`)
      .join('\n');

    return `STATIC TOKENS:
- Pricing reference → ${pricingLine}
- Objection strategies →
${objectionLines}`;
  }

  buildPlaybookContext(userProfile, cvAnalysis, scoringResults) {
    const targetCountry = userProfile.countrySelection?.selectedCountry || userProfile.personalInfo?.targetCountry || 'Germany';
    const bestPathway = scoringResults.bestMatch?.result;
    const selectedPathway = userProfile.reviewData?.selectedPathway || bestPathway?.pathwayName || 'Not specified';
    const pricingToken = this.resolvePricingToken(targetCountry, selectedPathway);

    const trimmedStrengths = this.preparePromptList(cvAnalysis?.immigrationStrengths, MAX_LIST_ITEMS);
    const trimmedConcerns = this.preparePromptList(cvAnalysis?.potentialConcerns, MAX_LIST_ITEMS);
    const trimmedObjections = this.preparePromptList(cvAnalysis?.salesObjections, MAX_LIST_ITEMS);
    const trimmedProjects = this.preparePromptList(cvAnalysis?.projectsAndAchievements, MAX_LIST_ITEMS);
    const trimmedSkills = this.preparePromptList(
      cvAnalysis?.skillsEvaluation?.technicalSkills?.inDemandSkills ||
      cvAnalysis?.skillsEvaluation?.technicalSkills ||
      [],
      MAX_LIST_ITEMS
    );

    const qualificationScore = this.sanitizeNumber(
      cvAnalysis?.selectedPathwayAnalysis?.eligibilityScore ||
      bestPathway?.percentageScore ||
      scoringResults?.bestMatch?.score
    );

    return {
      leadProfile: {
        name: `${userProfile.personalInfo?.firstName || ''} ${userProfile.personalInfo?.lastName || ''}`.trim(),
        age: userProfile.personalInfo?.age || 'Not specified',
        country: targetCountry,
        selectedPathway,
        pathwayId: pricingToken.token,
        budget: this.formatCurrency(userProfile.financialInfo?.educationBudget || userProfile.financialInfo?.totalBudget),
        language: {
          english: userProfile.languageSkills?.englishProficiency || userProfile.languageSkills?.english || 'Unknown',
          german: userProfile.languageSkills?.germanLevel || 'Not provided',
          ieltsScore: userProfile.languageSkills?.ieltsScore || userProfile.languageSkills?.hasIELTS
        },
        contactPreference: userProfile.personalInfo?.preferredContact || 'email'
      },
      scoring: {
        bestPathwayScore: qualificationScore,
        pathwayConfidence: scoringResults?.bestMatch?.result?.confidence,
        processingDays: scoringResults?.bestMatch?.result?.processingDays,
        totalScore: scoringResults?.bestMatch?.result?.totalScore
      },
      cvHighlights: {
        education: {
          level: cvAnalysis?.educationAssessment?.level,
          recognition: cvAnalysis?.educationAssessment?.recognition,
          institutions: this.preparePromptList(cvAnalysis?.educationAssessment?.institutions, MAX_LIST_ITEMS)
        },
        experience: {
          totalYears: cvAnalysis?.experienceAnalysis?.totalYears,
          relevantExperience: cvAnalysis?.experienceAnalysis?.relevantExperience,
          positions: this.preparePromptList(cvAnalysis?.experienceAnalysis?.positions, 3)
        },
        skills: trimmedSkills,
        projects: trimmedProjects
      },
      strengths: trimmedStrengths,
      objections: trimmedObjections.length ? trimmedObjections : trimmedConcerns,
      riskSignals: trimmedConcerns,
      recommendedAlternatives: this.preparePromptList(cvAnalysis?.recommendedPathways, 2),
      staticTokens: {
        pricing: pricingToken,
        objections: this.getObjectionTokens()
      }
    };
  }

  buildAnalyticsContext(userProfile, cvAnalysis, scoringResults) {
    const targetCountry = userProfile.countrySelection?.selectedCountry || userProfile.personalInfo?.targetCountry || 'Germany';
    const bestPathway = scoringResults?.bestMatch?.result;
    const selectedPathway = userProfile.reviewData?.selectedPathway || bestPathway?.pathwayName || 'Not specified';
    const pricingToken = this.resolvePricingToken(targetCountry, selectedPathway);
    const qualificationScore = this.sanitizeNumber(bestPathway?.percentageScore) || 0;
    const budgetValue = this.sanitizeNumber(userProfile.financialInfo?.educationBudget || userProfile.financialInfo?.totalBudget);

    return {
      lead: {
        name: `${userProfile.personalInfo?.firstName || ''} ${userProfile.personalInfo?.lastName || ''}`.trim(),
        email: userProfile.personalInfo?.email,
        country: targetCountry,
        canonicalCountry: this.normalizeCountry(targetCountry),
        pathwayLabel: selectedPathway,
        pathwayToken: pricingToken.token,
        qualificationScore,
        pricing: pricingToken,
        budgetDisplay: this.formatCurrency(budgetValue),
        budgetValue,
        languageScore: userProfile.languageSkills?.ieltsScore || userProfile.languageSkills?.englishProficiency,
        age: userProfile.personalInfo?.age
      },
      scoring: {
        maxScore: bestPathway?.maxPossibleScore,
        totalScore: bestPathway?.totalScore,
        successRate: bestPathway?.successRate,
        processingDays: bestPathway?.processingDays
      },
      cvSummary: {
        educationLevel: cvAnalysis?.educationAssessment?.level,
        institutions: this.preparePromptList(cvAnalysis?.educationAssessment?.institutions, 3),
        experienceYears: cvAnalysis?.experienceAnalysis?.totalYears,
        relevantExperience: cvAnalysis?.experienceAnalysis?.relevantExperience,
        strongestSkills: this.preparePromptList(
          cvAnalysis?.skillsEvaluation?.technicalSkills?.inDemandSkills ||
          cvAnalysis?.skillsEvaluation?.technicalSkills,
          MAX_LIST_ITEMS
        ),
        strengths: this.preparePromptList(cvAnalysis?.immigrationStrengths, MAX_LIST_ITEMS),
        concerns: this.preparePromptList(cvAnalysis?.potentialConcerns, MAX_LIST_ITEMS)
      },
      deterministicRisks: this.deriveRiskFactorsFromProfile(userProfile, scoringResults),
      financialSignals: {
        monthlyIncome: this.sanitizeNumber(userProfile.financialInfo?.monthlyIncome),
        fundsAvailable: this.sanitizeNumber(userProfile.financialInfo?.availableFunds || userProfile.financialInfo?.totalBudget),
        financingNeeded: Boolean(userProfile.financialInfo?.requiresLoan)
      }
    };
  }

  buildTimelineContext(timelineContext) {
    const crmEvents = this.normalizeCrmEvents(timelineContext.crmEvents || timelineContext.activities || []);
    return {
      ...timelineContext,
      crmEvents: crmEvents.slice(0, MAX_LIST_ITEMS),
      milestoneCountTarget: Math.min(
        Math.max(crmEvents.length + 3, 6),
        MAX_TIMELINE_MILESTONES
      )
    };
  }

  buildTimelinePrompt(timelineContext) {
    const { leadInfo, salesMetrics, currentStatus, crmEvents = [], milestoneCountTarget } = timelineContext;
    const crmSummary = crmEvents.length
      ? crmEvents.map(event =>
          `- ${event.date} | ${event.title} | status: ${event.status} | notes: ${event.notes || 'n/a'}`
        ).join('\n')
      : 'None logged';

    return `
You are a conversion timeline strategist. Build a milestone plan grounded in CRM history—never invent past events.

LEAD SNAPSHOT:
- Name: ${leadInfo?.name || 'Lead'}
- Country: ${leadInfo?.targetCountry}
- Pathway: ${leadInfo?.pathway}
- Qualification Score: ${leadInfo?.matchScore || 0}%
- Current Status: ${currentStatus}

SALES METRICS:
- Conversion Probability: ${salesMetrics?.conversionProbability || 'n/a'}%
- Estimated Deal Size: ${salesMetrics?.estimatedDealSize || 'n/a'}
- Close Timeframe Target: ${salesMetrics?.closeTimeframe || 'n/a'}

RECENT CRM EVENTS (most recent first):
${crmSummary}

REQUIREMENTS:
- Produce ${milestoneCountTarget} milestones maximum.
- Stage titles must be action-oriented, <= 40 characters, and clearly indicate ownership.
- Completed milestones must align with CRM events above; do NOT fabricate completed stages.
- Provide ISO 8601 dates (YYYY-MM-DD). If uncertain, estimate relative to the most recent CRM date.
- Duration should be plain language (e.g., "3 business days").
- keyActions must contain 1-4 verb-led tasks referencing documentation, payments, or scheduling commitments.
- successCriteria must describe measurable proof that the milestone is complete.
- Ensure stage names are unique and ordered chronologically.

Return ONLY a JSON array of milestones with fields:
stage, status (completed/current/pending), description, estimatedDate, duration, keyActions[], successCriteria.
`;
  }

  normalizeCrmEvents(events) {
    if (!Array.isArray(events)) return [];
    return events
      .map((event) => {
        const timestamp = event.timestamp || event.date || event.created_at;
        const parsedDate = timestamp ? new Date(timestamp) : null;
        return {
          title: event.title || event.stage || event.type || 'Activity',
          status: event.status || event.outcome || 'pending',
          date: parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate.toISOString() : null,
          notes: this.truncate(event.notes || event.description || ''),
          actor: event.assignee || event.agent || 'Sales',
          type: event.type || event.stage || 'touchpoint'
        };
      })
      .filter((event) => event.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  buildTimelineFallbackEvents(crmEvents, leadInfo = {}) {
    if (crmEvents.length) {
      const parsed = crmEvents.map((event, index) => ({
        stage: event.title || `Milestone ${index + 1}`,
        status: event.status === 'completed' ? 'completed' : index === crmEvents.length - 1 ? 'current' : 'completed',
        description: event.notes || 'Milestone completed.',
        estimatedDate: event.date,
        duration: '1 day',
        keyActions: [event.notes || `${event.type} logged by ${event.actor}`],
        successCriteria: 'Logged in CRM'
      }));
      return parsed;
    }

    const baseDate = new Date();
    return [
      {
        stage: 'Initial Contact Logged',
        status: 'completed',
        description: 'Lead submitted inquiry and was added to CRM.',
        estimatedDate: baseDate.toISOString(),
        duration: 'Same day',
        keyActions: ['Lead capture confirmed', 'Initial qualification note recorded'],
        successCriteria: 'Lead status updated to Qualified'
      }
    ];
  }

  buildAnalyticsPrompt(userProfile, cvAnalysis, scoringResults) {
    const analyticsContext = this.buildAnalyticsContext(userProfile, cvAnalysis, scoringResults);
    const playbookContext = this.buildPlaybookContext(userProfile, cvAnalysis || {}, scoringResults || {});
    const payload = {
      analyticsContext,
      strengths: playbookContext.strengths,
      objections: playbookContext.objections,
      riskSignals: playbookContext.riskSignals,
      recommendedAlternatives: playbookContext.recommendedAlternatives
    };

    const pricingLine = `${analyticsContext.lead.pricing.token} (${analyticsContext.lead.pricing.price}; ${analyticsContext.lead.pricing.paymentStructure})`;

    return `
You are the analytics lead for Ravana Migration Services. Produce deterministic, sales-ready analytics grounded ONLY in the payload below.

STATIC REFERENCES:
- Pricing token → ${pricingLine}
- Canonical pathway id → ${analyticsContext.lead.pathwayToken}
- Deterministic risk signals → ${JSON.stringify(analyticsContext.deterministicRisks)}

PAYLOAD:
${JSON.stringify(payload, null, 2)}

RESPONSE REQUIREMENTS:
- Return ONLY JSON matching the analytics schema (conversionMetrics, marketInsights, competitorAnalysis, riskFactors, opportunityScore, recommendedStrategy, priorityScore).
- Numbers MUST be numeric types (no strings) where applicable.
- Risk factors must include severity (High/Medium/Low) and actionable mitigation.
- Blend AI insight with deterministic risk data—never contradict provided facts.
- Use ${analyticsContext.lead.pricing.token} for pricing references instead of repeating raw text.

Output JSON now.`;
  }

  stripCodeFences(text) {
    if (typeof text !== 'string') return '';
    let cleaned = text.trim();
    if (cleaned.startsWith('```json')) {
      cleaned = cleaned.replace(/^```json\s*\n?/, '').replace(/\n?```\s*$/, '');
    } else if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```\s*\n?/, '').replace(/\n?```\s*$/, '');
    }
    return cleaned.trim();
  }

  safeJsonParse(raw, fallbackFactory, contextLabel = 'response') {
    if (!raw) {
      return typeof fallbackFactory === 'function' ? fallbackFactory() : fallbackFactory;
    }

    const cleaned = this.stripCodeFences(raw);
    const candidates = [];

    if (cleaned) {
      candidates.push(cleaned);
      const firstBrace = cleaned.indexOf('{');
      const lastBrace = cleaned.lastIndexOf('}');
      const firstBracket = cleaned.indexOf('[');
      const lastBracket = cleaned.lastIndexOf(']');
      if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
        candidates.push(cleaned.slice(firstBrace, lastBrace + 1));
      }
      if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
        candidates.push(cleaned.slice(firstBracket, lastBracket + 1));
      }
    }

    for (const candidate of candidates) {
      try {
        return JSON.parse(candidate);
      } catch (error) {
        // Continue to next candidate
      }
    }

    console.warn(`⚠️ Unable to parse ${contextLabel}; using fallback.`);
    return typeof fallbackFactory === 'function' ? fallbackFactory() : fallbackFactory;
  }

  limitArray(values, limit = 5) {
    if (!Array.isArray(values)) return [];
    return values.filter((item) => item !== null && item !== undefined).slice(0, limit);
  }

  validateSalesPlaybook(playbook, fallback) {
    if (!playbook || typeof playbook !== 'object') {
      return fallback;
    }

    const safePlaybook = { ...fallback, ...playbook };
    safePlaybook.keyTalkingPoints = this.limitArray(safePlaybook.keyTalkingPoints || fallback.keyTalkingPoints, 6);
    safePlaybook.objectionHandling = this.limitArray(safePlaybook.objectionHandling || fallback.objectionHandling, 5);
    safePlaybook.nextSteps = this.limitArray(safePlaybook.nextSteps || fallback.nextSteps, 8);
    return safePlaybook;
  }

  validateAnalyticsResponse(analytics, userProfile, scoringResults) {
    const fallback = this.buildFallbackAnalytics(userProfile, scoringResults);
    if (!analytics || typeof analytics !== 'object') {
      return fallback;
    }

    const parsed = ANALYTICS_SCHEMA.safeParse(analytics);
    if (!parsed.success) {
      this.debug('Analytics schema validation failed', parsed.error.flatten());
      return fallback;
    }

    const normalized = parsed.data;
    const safeAnalytics = { ...fallback, ...normalized };
    safeAnalytics.conversionMetrics = {
      ...fallback.conversionMetrics,
      ...normalized.conversionMetrics
    };
    safeAnalytics.riskFactors = this.limitArray(normalized.riskFactors, 5);
    if (!safeAnalytics.riskFactors.length) {
      safeAnalytics.riskFactors = this.deriveRiskFactorsFromProfile(userProfile, scoringResults);
    }
    safeAnalytics.recommendedStrategy = {
      ...fallback.recommendedStrategy,
      ...normalized.recommendedStrategy
    };
    safeAnalytics.priorityScore = {
      ...fallback.priorityScore,
      ...normalized.priorityScore
    };
    return safeAnalytics;
  }

  validateTimelineResponse(timeline, timelineContext) {
    if (!Array.isArray(timeline) || !timeline.length) {
      return this.buildFallbackTimeline(timelineContext);
    }

    const sanitized = timeline
      .filter(milestone => milestone && typeof milestone === 'object')
      .map((milestone, index) => ({
        stage: milestone.stage || `Milestone ${index + 1}`,
        status: milestone.status || (index === 0 ? 'completed' : 'pending'),
        description: milestone.description || '',
        estimatedDate: milestone.estimatedDate || this.addDaysToDate(new Date(), (index + 1) * 2),
        duration: milestone.duration || '1 day',
        keyActions: this.limitArray(milestone.keyActions || [], 4),
        successCriteria: milestone.successCriteria || 'Milestone achieved'
      }));

    const deduped = [];
    const seenStages = new Set();
    sanitized
      .sort((a, b) => new Date(a.estimatedDate) - new Date(b.estimatedDate))
      .forEach((milestone) => {
        let stage = milestone.stage;
        let counter = 2;
        while (seenStages.has(stage)) {
          stage = `${milestone.stage} (${counter++})`;
        }
        seenStages.add(stage);
        deduped.push({ ...milestone, stage });
      });

    const targetLength = timelineContext?.milestoneCountTarget || MAX_TIMELINE_MILESTONES;
    const clamped = deduped.slice(0, targetLength);

    if (Array.isArray(timelineContext?.crmEvents) && timelineContext.crmEvents.length) {
      const completedCount = timelineContext.crmEvents.filter(event => event.status === 'completed').length;
      clamped.forEach((milestone, index) => {
        if (index < completedCount) {
          milestone.status = 'completed';
        } else if (index === completedCount) {
          milestone.status = 'current';
        } else {
          milestone.status = 'pending';
        }
      });
    }

    return clamped;
  }

  buildFallbackSalesPlaybook(userProfile, scoringResults) {
    const targetCountry = userProfile.countrySelection?.selectedCountry || 'Germany';
    const selectedPathway = userProfile.reviewData?.selectedPathway || scoringResults?.bestMatch?.result?.pathwayName || 'Selected Immigration Pathway';

    return {
      recommendedApproach: `Focus the consultation on ${selectedPathway} in ${targetCountry}, reinforcing the candidate's existing interest and showing the clearest path to approval.`,
      keyTalkingPoints: [
        `Highlight ${selectedPathway} success rate and typical timelines in ${targetCountry}.`,
        'Showcase Ravana\'s end-to-end support from documentation to visa interview.',
        'Present transparent pricing with the split payment structure to reduce upfront anxiety.'
      ],
      objectionHandling: [],
      nextSteps: [
        'Schedule a detailed consultation within 48 hours.',
        'Send a concise document checklist tailored to the lead profile.',
        'Share at least one success story from a comparable candidate.'
      ]
    };
  }

  buildFallbackAnalytics(userProfile, scoringResults) {
    const conversionProbability = scoringResults?.bestMatch?.percentageScore || 50;
    const estimatedFee = scoringResults?.bestMatch?.result?.baseCost ? scoringResults.bestMatch.result.baseCost * 1000 : 1000000;
    return {
      conversionMetrics: {
        conversionProbability,
        conversionLevel: conversionProbability >= 70 ? 'Hot' : conversionProbability >= 40 ? 'Warm' : 'Cold',
        estimatedDealSize: estimatedFee,
        estimatedCommission: Math.round(estimatedFee * 0.25),
        closeTimeframe: conversionProbability >= 70 ? '3-7 days' : '7-21 days',
        confidenceLevel: conversionProbability >= 70 ? 'High' : conversionProbability >= 40 ? 'Medium' : 'Low',
        conversionBlockers: []
      },
      riskFactors: this.deriveRiskFactorsFromProfile(userProfile, scoringResults),
      opportunityScore: {
        overall: conversionProbability,
        breakdown: {
          profileStrength: conversionProbability,
          budgetAlignment: 60,
          urgencyLevel: 50,
          conversionLikelihood: conversionProbability
        },
        reasoning: 'Baseline analytics derived from scoring engine results.'
      },
      recommendedStrategy: {
        approachType: 'Consultative',
        keyFocusAreas: ['Reinforce pathway viability', 'Clarify payment structure', 'Surface success stories'],
        pricingStrategy: 'Position service fee as investment tied to offer letter milestones.',
        timelineStrategy: 'Use upcoming intake deadlines to drive urgency.',
        conversionTactics: [],
        expectedChallenges: [],
        successFactors: [],
        nextBestAction: 'Book strategy call within 48 hours.'
      },
      priorityScore: {
        salesPriority: conversionProbability >= 70 ? 'High' : 'Medium',
        reasoning: 'Automatically assigned based on eligibility score and budget data availability.',
        recommendedFollowUp: 'Follow up via call within 24 hours and send proposal summary email.'
      }
    };
  }

  buildFallbackTimeline(timelineContext) {
    const startDate = new Date();
    const addDays = (days) => this.addDaysToDate(startDate, days);
    return [
      {
        stage: 'Initial Contact',
        status: 'completed',
        description: 'Lead submitted interest form and initial qualification was captured.',
        estimatedDate: addDays(0),
        duration: '1 day',
        keyActions: [
          'Send welcome email',
          'Confirm consultation availability'
        ],
        successCriteria: 'Lead acknowledges outreach and books consultation.'
      },
      {
        stage: 'Consultation & Proposal',
        status: 'current',
        description: 'Detailed pathway review and proposal preparation.',
        estimatedDate: addDays(3),
        duration: '2 days',
        keyActions: [
          'Present tailored pathway strategy',
          'Share document checklist'
        ],
        successCriteria: 'Lead confirms interest and requests timeline.'
      },
      {
        stage: 'Decision & Contract',
        status: 'pending',
        description: 'Handle objections and finalize service agreement.',
        estimatedDate: addDays(8),
        duration: '3 days',
        keyActions: [
          'Address outstanding concerns',
          'Prepare invoice and contract'
        ],
        successCriteria: 'Contract signed and initial payment scheduled.'
      }
    ];
  }

  deriveRiskFactorsFromProfile(userProfile, scoringResults) {
    const factors = [];

    if (!userProfile.financialInfo?.availableFunds && !userProfile.financialInfo?.totalBudget) {
      factors.push({
        factor: 'Budget Uncertainty',
        severity: 'High',
        impact: 'May delay commitment until financing is confirmed',
        mitigation: 'Highlight flexible payment schedules and Canada loan facility'
      });
    }

    const experienceYears = parseFloat(userProfile.experience?.totalYears || userProfile.experience_info?.totalExperience || 0);
    if (!Number.isFinite(experienceYears) || experienceYears < 2) {
      factors.push({
        factor: 'Limited Professional Experience',
        severity: 'Medium',
        impact: 'Could affect visa eligibility for work-focused pathways',
        mitigation: 'Position study pathways or skills upgrading plans'
      });
    }

    const englishLevel = (userProfile.languageSkills?.english || '').toLowerCase();
    if (englishLevel && englishLevel !== 'fluent') {
      factors.push({
        factor: 'Language Proficiency',
        severity: 'Medium',
        impact: 'May require IELTS preparation prior to application',
        mitigation: 'Offer IELTS support and highlight minimal requirements for English programs'
      });
    }

    if (!factors.length) {
      factors.push({
        factor: 'General Due Diligence',
        severity: 'Low',
        impact: 'Standard risks around documentation accuracy and deadlines',
        mitigation: 'Follow Ravana documentation checklist and review cadence'
      });
    }

    return factors;
  }

  addDaysToDate(date, days) {
    const base = new Date(date);
    base.setDate(base.getDate() + days);
    return base.toISOString().split('T')[0];
  }

  /**
   * Build fallback CV analysis when AI parsing fails
   */
  buildFallbackCVAnalysis(userProfile, cvTextOrResponse) {
    return {
      educationAssessment: {
        level: userProfile?.education?.highestEducation || "To be determined",
        institutions: [],
        fields: [],
        recognition: "Pending full analysis",
        relevance: "Standard evaluation required",
        institutionQuality: "Unknown",
        gpaStrength: "Not specified",
        additionalEducation: []
      },
      experienceAnalysis: {
        positions: [],
        totalYears: userProfile?.experience?.totalExperience || "0",
        relevantExperience: "0",
        careerProgression: "Standard profile",
        industryDemand: "Moderate",
        seniority: "Entry to Mid-level",
        companyReputation: "Standard",
        employmentGaps: "None identified",
        currentSalary: "Not disclosed",
        leadershipExperience: "To be verified"
      },
      skillsEvaluation: {
        technicalSkills: {
          programming: [],
          frameworks: [],
          software: [],
          specializations: [],
          inDemandSkills: []
        },
        languageSkills: {
          english: userProfile?.languageSkills?.englishProficiency || "Unknown",
          german: "Not specified",
          otherLanguages: [],
          languageAdvantage: "IELTS assessment recommended"
        },
        softSkills: [],
        certifications: [],
        professionalMemberships: []
      },
      projectsAndAchievements: {
        notableProjects: [],
        awards: [],
        publications: [],
        speakingEngagements: []
      },
      immigrationStrengths: [
        "Profile under review",
        "Standard eligibility assessment pending"
      ],
      potentialConcerns: [
        "Complete documentation review required",
        "Language proficiency verification needed"
      ],
      selectedPathwayAnalysis: {
        pathway: userProfile?.reviewData?.selectedPathway || "To be determined",
        country: userProfile?.countrySelection?.selectedCountry || "Germany",
        eligibilityScore: 50,
        scoreBreakdown: {
          education: 10,
          experience: 10,
          language: 10,
          financialReadiness: 10,
          ageOptimality: 10
        },
        eligible: "Fair",
        strengths: ["Standard profile"],
        weaknesses: ["Requires detailed assessment"],
        missingRequirements: ["Complete documentation"],
        timelineEstimate: "6-12 months",
        successProbability: 50,
        improvementActions: ["Submit complete documentation", "Language proficiency test"]
      },
      alternativePathways: [],
      salaryExpectations: {
        currentSalary: "Not specified",
        targetCountrySalary: "Market dependent",
        competitiveness: "Standard"
      },
      missingCriticalInfo: [
        "Detailed work experience",
        "Language test scores",
        "Educational credentials"
      ],
      overallAssessment: cvTextOrResponse ?
        `Analysis partially completed. Profile shows standard eligibility for immigration pathways. Full assessment requires additional documentation and verification.` :
        "Complete profile assessment pending. Standard immigration pathway eligibility."
    };
  }

  async generateConsultationReply(conversationTranscript, contextPayload, retries = 3) {
    try {
      const transcript = conversationTranscript && conversationTranscript.trim().length > 0
        ? conversationTranscript
        : 'No prior conversation.';

      const contextBlock = JSON.stringify(contextPayload, null, 2);
      const userContent = `${transcript}\n\n<<CONTEXT>>\n${contextBlock}`;

      const response = await this.client.chat.completions.create({
        messages: [
          { role: "system", content: CONSULT_SYSTEM_PROMPT },
          { role: "user", content: userContent }
        ],
        model: this.modelName,
        temperature: this.getModelSupportsTemperature() ? 0.4 : 1,
        max_completion_tokens: 900
      });

      const aiContent = response?.choices?.[0]?.message?.content || '';
      return this.safeJsonParse(
        aiContent,
        () => ({
          reply: this.buildFallbackConsultReply(contextPayload),
          dataUpdates: {},
          pendingFields: contextPayload?.pendingFields || [],
          shouldComplete: false,
          nextAction: "ask_question"
        }),
        'consultation chat'
      );
    } catch (error) {
      console.error('Error generating consultation reply:', error);

      // If rate limit error and retries remaining, wait and retry with exponential backoff
      if (error.status === 429 && retries > 0) {
        const waitTime = (4 - retries) * 2000; // 2s, 4s, 6s backoff
        console.log(`⏳ Rate limited. Waiting ${waitTime/1000}s before retry (${retries} retries left)...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return this.generateConsultationReply(conversationTranscript, contextPayload, retries - 1);
      }

      // If rate limit error persists after all retries
      if (error.status === 429) {
        return {
          reply: "I'm experiencing high traffic right now. Please wait a moment and try again. Your information is safe and we'll continue from where we left off.",
          dataUpdates: {},
          pendingFields: contextPayload?.pendingFields || [],
          shouldComplete: false,
          nextAction: 'ask_question'
        };
      }

      return {
        reply: this.buildFallbackConsultReply(contextPayload),
        dataUpdates: {},
        pendingFields: contextPayload?.pendingFields || [],
        shouldComplete: false,
        nextAction: 'ask_question'
      };
    }
  }

  getModelSupportsTemperature() {
    try {
      const name = (this.modelName || '').toLowerCase();
      // Older Azure-only deployments ignored temperature; Gemini fully supports it
      if (name.includes('gpt-5-mini')) return false;
      if (name.includes('deepseek')) return false;
      return true;
    } catch (_) {
      return true;
    }
  }

  buildFallbackConsultReply(context) {
    try {
      const pending = Array.isArray(context?.pendingFields) ? context.pendingFields : [];
      const langPref = (context?.languagePreference || '').toString().trim();
      const cvUploaded = Boolean(context?.cvUploaded);

      // If language not set, ask for it explicitly
      if (!langPref || langPref.toLowerCase() === 'english' && pending.find(p => p.section === 'languagePreference')) {
        return 'To continue, please share your preferred language: English, Tamil, or Sinhala.';
      }

      // If CV not uploaded and other fields are mostly present, encourage CV upload
      if (!cvUploaded && pending.length <= 3) {
        return 'You can upload your CV now to speed things up. Meanwhile, could you share the next detail?';
      }

      // Ask for the first pending field in a concise way
      const nextField = pending[0];
      if (nextField?.label) {
        return `Could you share your ${nextField.label}?`;
      }

      // Generic friendly fallback
      return 'Thanks! Could you clarify that or share the next detail?';
    } catch (_) {
      return 'Thanks! Could you clarify that or share the next detail?';
    }
  }
}

export default AzureAIService;
